/**
 * Citation-aware inline text parsing, shared by {@link Markdown} (Blueprint
 * card bodies) and any other text surface that needs to render the
 * same `**bold**`/`*italic*`/`[[src:...]]`/`[[cite:...]]` inline grammar —
 * currently the documents diff viewer's word-diff runs. `[[cite:...]]`
 * is the unresolved-citation marker an imported profile's citations become
 * (see `profile_import.parser.rewrite_citations`): a text-only dagger,
 * never a link.
 *
 * Kept in its own module (rather than inside `Markdown.tsx`) so `renderInline`
 * — a plain function, not a component — can be exported without mixing
 * component and non-component exports in one file (the `Citation` marker
 * component itself lives in `./citationMarker` for the same reason).
 */
import React from "react";
import { parseSourceRef } from "./cardRanking";
import { Citation, UnresolvedCitation } from "./citationMarker";
import { InferenceMarker } from "./inferenceMarker";
import type { SourceCitation } from "../../types";

/** A parsed inline run: plain text, bold, italic, or a grounding citation. */
interface InlineRun {
  kind:
    | "text"
    | "bold"
    | "italic"
    | "citation"
    | "inference"
    | "unresolved-citation";
  value: string;
}

// Recognize bold/italic emphasis spans and `[[src:…]]` citation markers. The
// citation alternative is listed first so a citation immediately following an
// emphasis span is still captured as its own run. `__..__`/`_.._` (CommonMark
// underscore emphasis) come after the asterisk forms, `__` before `_` so a
// `__bold__` run isn't mis-split as nested italics; both are flanked by
// `(?<!\w)…(?!\w)` word-boundary guards so intraword underscores in
// identifiers (e.g. `chat_thread_id`) are never mistaken for emphasis.
//
// Every emphasis alternative excludes `\n`, because emphasis does not span
// lines in markdown and an unpaired `*`/`_` is ordinary text. Without that,
// a lone `*` (a bullet marker, a footnote asterisk) pairs with one on a LATER
// line and swallows everything between — eating the next line's bullet and
// merging two list items. `DiffView` hands whole multi-line word-diff runs to
// this parser, so a body of `* **Label:** …` bullets is exactly that shape.
const INLINE_PATTERN =
  /(\[\[src:[^\]]+\]\]|\[\[cite:[^\]]*\]\]|\[\[inf:[^\]]*\]\]|\*\*[^*\n]+\*\*|\*[^*\n]+\*|(?<!\w)__[^_\n]+__(?!\w)|(?<!\w)_[^_\n]+_(?!\w))/g;

// A bare (un-bracketed) `src:...` mention or a raw UUID that leaked into
// prose OUTSIDE a well-formed `[[src:...]]` marker (which INLINE_PATTERN
// above already extracts into its own "citation" run, never reaching these).
// Free-form LLM chat prose is not guaranteed to wrap every reference
// correctly (e.g. echoing a tool result's raw id) — this is a defensive
// backstop so the human-readable-UI rule holds even when it doesn't.
const STRAY_SRC_TOKEN_RE = /\bsrc:\S+/gi;
const STRAY_UUID_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/**
 * Strip a stray backend id that leaked into plain prose outside a citation
 * marker. Never touches text inside an already-extracted `[[src:...]]`
 * citation run (those are parsed out atomically before this ever runs).
 *
 * @param text - A plain/bold/italic inline run's text.
 * @returns The text with any stray `src:`/UUID token removed.
 */
function sanitizeStrayIdentifiers(text: string): string {
  return text
    .replace(STRAY_SRC_TOKEN_RE, "the cited source")
    .replace(STRAY_UUID_RE, "")
    .replace(/[ \t]{2,}/g, " ");
}

/**
 * Split text into styled inline runs.
 *
 * Emphasis is matched within a single line (see {@link INLINE_PATTERN}), so
 * multi-line text is safe to pass: an unpaired `*`/`_` stays plain text
 * rather than pairing across a newline.
 *
 * @param text - The raw text.
 * @returns Ordered inline runs preserving surrounding plain text.
 */
function parseInline(text: string): InlineRun[] {
  const runs: InlineRun[] = [];
  const parts = text.split(INLINE_PATTERN);
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part === "") continue;

    // Odd indices are regex captures (matched emphasis/citations).
    if (i % 2 !== 0) {
      if (part.startsWith("[[src:") && part.endsWith("]]")) {
        // Strip the `[[src:` prefix and `]]` suffix → the bare citation ref.
        runs.push({ kind: "citation", value: part.slice(6, -2) });
      } else if (part.startsWith("[[cite:") && part.endsWith("]]")) {
        // An unresolved profile citation: the raw tag text stays reachable.
        runs.push({ kind: "unresolved-citation", value: part.slice(7, -2) });
      } else if (part.startsWith("[[inf:")) {
        // `[[inf:]]` with no ids is valid and meaningful: the sentence is still
        // an interpretation even when no single card carries it.
        runs.push({ kind: "inference", value: part.slice(6, -2) });
      } else if (part.startsWith("**") && part.endsWith("**")) {
        runs.push({ kind: "bold", value: part.slice(2, -2) });
      } else if (part.startsWith("*") && part.endsWith("*")) {
        runs.push({ kind: "italic", value: part.slice(1, -1) });
      } else if (part.startsWith("__") && part.endsWith("__")) {
        runs.push({ kind: "bold", value: part.slice(2, -2) });
      } else {
        // The regex guarantees the only remaining match is `_..._`.
        runs.push({ kind: "italic", value: part.slice(1, -1) });
      }
    } else {
      // Even indices are non-matched plain text.
      runs.push({ kind: "text", value: part });
    }
  }
  return runs;
}

/**
 * Build a clinician-facing citation label from a raw marker ref.
 *
 * Raw refs look like `SES-1#L3-L7` — the part before `#` is the source id and
 * the `#L<a>-L<b>` suffix is the line range. The UI serves non-technical
 * clinicians, so the raw source id is NEVER shown. When `sourceTitles` resolves
 * the id to a human title, the label reads "Source: {title}, lines 3–7";
 * otherwise it falls back to the neutral "Source · lines 3–7" (still never the
 * raw id).
 *
 * @param value - The raw citation ref (id + optional `#L<a>-L<b>` range).
 * @param sourceTitles - Optional `{ "<source id>": "<title>" }` lookup.
 * @returns A clean, human-readable citation label.
 */
// Exported so the TipTap citation node view labels its marker identically to
// the read-only renderer — one citation vocabulary across both surfaces.
/**
 * Resolve an inference marker's `+`-joined card ids to displayable cards.
 *
 * An id with no known name is dropped rather than shown raw — the
 * clinician-simple rule forbids surfacing a backend id, and a marker with an
 * empty list still communicates "this is an interpretation".
 *
 * @param value - The marker's inner text (`<id>+<id>`, possibly empty).
 * @param cardTitles - Optional `{ card id: name }` lookup.
 * @returns The resolvable cards, in marker order.
 */
export function resolveCards(
  value: string,
  cardTitles?: Record<string, string>,
): { id: string; name: string }[] {
  return value
    .split("+")
    .filter((id) => id !== "")
    .map((id) => ({ id, name: cardTitles?.[id] ?? "" }))
    .filter((card) => card.name !== "");
}

export function citationLabel(
  value: string,
  sourceTitles?: Record<string, string>,
): string {
  const { title, lines } = parseSourceRef(value, sourceTitles);
  if (title !== null) {
    return lines ? `Source: ${title}, ${lines}` : `Source: ${title}`;
  }
  return lines ? `Source · ${lines}` : "Source";
}

/**
 * Render text as citation-aware inline React nodes.
 *
 * Parses `**bold**`/`*italic*`/`[[src:...]]` runs (see {@link parseInline})
 * and defensively strips a stray backend id from any plain/bold/italic run
 * (see {@link sanitizeStrayIdentifiers}) so no caller needs its own ad-hoc
 * raw-text rendering. Callers may pass a whole multi-line run (as `DiffView`
 * does) — emphasis never pairs across a newline.
 *
 * @param text - The raw text (one line, or a multi-line run).
 * @param sourceTitles - Optional `{ "<source id>": "<title>" }` lookup for
 *   resolving citation source names.
 * @param citations - Optional resolved structured-citation lookup (explicit
 *   cited lines + verbatim snippet) keyed by citation ref.
 * @param cardTitles - Optional `{ "<card id>": "<name>" }` lookup so an
 *   inference marker can name the cards it was drawn from. A raw `ENT-` id is
 *   never rendered; an unresolvable id is dropped from the list.
 * @returns A fragment of styled inline nodes.
 */
export function renderInline(
  text: string,
  sourceTitles?: Record<string, string>,
  citations?: Map<string, SourceCitation>,
  cardTitles?: Record<string, string>,
  rawIdentifiers: boolean = false,
): React.ReactNode {
  return parseInline(text).map((run, index) => {
    const sanitize = (val: string): string =>
      rawIdentifiers ? val : sanitizeStrayIdentifiers(val);

    if (run.kind === "bold") {
      return <strong key={index}>{sanitize(run.value)}</strong>;
    }
    if (run.kind === "italic") {
      return <em key={index}>{sanitize(run.value)}</em>;
    }
    if (run.kind === "citation") {
      return (
        <Citation
          key={index}
          value={run.value}
          label={citationLabel(run.value, sourceTitles)}
          citation={citations?.get(run.value)}
        />
      );
    }
    if (run.kind === "inference") {
      return (
        <InferenceMarker
          key={index}
          cards={resolveCards(run.value, cardTitles)}
        />
      );
    }
    if (run.kind === "unresolved-citation") {
      return <UnresolvedCitation key={index} tagText={run.value} />;
    }
    return <React.Fragment key={index}>{sanitize(run.value)}</React.Fragment>;
  });
}
